import { useMemberPermissionsQuery } from '@/hooks/queries/usePermissionsQuery'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'

import {
  getPermissionModuleType,
  ModuleTypeEnum,
  PermissionActionEnum,
} from '@scrambl/shared'

/**
 * Custom hook for managing permissions in components
 * For external org access, permissions are checked via the permission set on the relationship
 */
export function usePermissions() {
  // Subscribe to externalOrgRelationship directly so the hook re-renders when it changes
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const externalOrgRelationship = useOrganizationStore(
    state => state.externalOrgRelationship,
  )
  const getCurrentUserMembership = useOrganizationStore(
    state => state.getCurrentUserMembership,
  )
  const { user } = useAuth()
  const isExternal = currentOrganization?.isExternalAccess === true

  const currentMembership = getCurrentUserMembership()

  // Organization owners and admins don't need to fetch permissions - they have all permissions
  // Check both the getter and direct access for reliability during initialization
  const memberRole = (
    currentMembership?.role || currentOrganization?.userMembership?.role
  )?.toUpperCase()
  const membership = currentMembership ?? currentOrganization?.userMembership
  const isActiveMember = membership?.status === 'ACTIVE'
  const isAdminOrOwner =
    !isExternal &&
    isActiveMember &&
    (memberRole === 'OWNER' || memberRole === 'ADMIN')

  // Get membership ID from both sources for reliability
  const effectiveMembershipId =
    currentMembership?.id || currentOrganization?.userMembership?.id

  // Fetch current user's permissions using query hook (only for non-admin/owner members)
  // Disabled for external org access - permissions are handled server-side via permission set
  const requiresMemberPermissions =
    !!user?.id &&
    !!currentOrganization?.id &&
    isActiveMember &&
    !!effectiveMembershipId &&
    !isAdminOrOwner &&
    !isExternal

  const { data: memberPermissionsData, isPending: isMemberPermissionsPending } =
    useMemberPermissionsQuery(effectiveMembershipId, {
      enabled: requiresMemberPermissions,
    })

  // `isLoading` excludes restore-paused queries. Treat a required permission
  // query as loading for its entire pending state so route/page denial checks
  // cannot act on an empty permission map during cache hydration. Disabled
  // queries (admins, owners, and external users) remain ready immediately.
  const isLoadingMemberPermissions =
    requiresMemberPermissions && isMemberPermissionsPending

  /**
   * Check if the current user has a specific permission
   * Uses query data directly instead of store to avoid timing issues
   * For external org access, permissions are checked server-side via permission set
   */
  const checkPermission = (
    requestedModule: ModuleTypeEnum,
    action: PermissionActionEnum,
  ): boolean => {
    // A sub-table (an Inventory tab, say) has its own module type so that its
    // views and columns are scoped to it, but it is not separately grantable:
    // it answers with its parent module's permissions.
    const module = getPermissionModuleType(requestedModule)

    if (!user?.id || !currentOrganization?.id) return false

    // Resolve the selected partner before any direct-member shortcut.
    if (isExternal) {
      if (
        !externalOrgRelationship?.permissionSet ||
        externalOrgRelationship.hostOrganizationId !== currentOrganization.id ||
        externalOrgRelationship.status !== 'ACTIVE' ||
        externalOrgRelationship.revokedAt
      )
        return false
      if (user.role === 'SYSTEM_ADMIN' || user.role === 'ADMIN') return true
      return (
        externalOrgRelationship.permissionSet.permissionSetItems?.some(
          item =>
            item.permissionDefinition?.moduleType === module &&
            item.permissionDefinition.allowedActions?.includes(action),
        ) ?? false
      )
    }

    if (!effectiveMembershipId || !isActiveMember) return false
    if (user.role === 'SYSTEM_ADMIN' || user.role === 'ADMIN') return true

    // Today is available through organization membership itself. Its widgets
    // still use their own module types when gating data or actions.
    if (
      module === ModuleTypeEnum.TODAY &&
      effectiveMembershipId &&
      !isExternal
    ) {
      return true
    }

    // Safe member identity reads (people directory, mentions, avatars, and
    // personal-member surfaces) follow direct organization membership. Member
    // administration is enforced separately by the OWNER/ADMIN organization
    // role and never by a permission-set definition.
    if (
      module === ModuleTypeEnum.MEMBER &&
      action === PermissionActionEnum.READ &&
      effectiveMembershipId &&
      !isExternal
    ) {
      return true
    }

    // Organization owners and admins have all permissions within their org
    if (isAdminOrOwner) {
      return true
    }

    // If permissions are still loading, default to FALSE to hide navigation items
    // This prevents showing modules the user doesn't have access to during load
    if (isLoadingMemberPermissions) {
      return false
    }

    // Read the selected membership query; never fall back to an unscoped store.
    if (memberPermissionsData) {
      // Check direct permissions
      const hasDirectPermission =
        memberPermissionsData.directPermissions?.some(
          perm =>
            perm.permissionDefinition?.moduleType === module &&
            perm.permissionDefinition?.allowedActions?.includes(action),
        ) || false

      if (hasDirectPermission) {
        return true
      }

      // Check permissions via permission sets
      const hasSetPermission =
        memberPermissionsData.permissionSets?.some(setAssignment =>
          setAssignment.permissionSet?.permissionSetItems?.some(
            item =>
              item.permissionDefinition?.moduleType === module &&
              item.permissionDefinition?.allowedActions?.includes(action),
          ),
        ) || false

      return hasSetPermission
    }

    return false
  }

  /**
   * Check if the current user can perform any of the given actions on a module
   */
  const checkAnyPermission = (
    module: ModuleTypeEnum,
    actions: PermissionActionEnum[],
  ): boolean => {
    return actions.some(action => checkPermission(module, action))
  }

  /**
   * Check if the current user can perform all of the given actions on a module
   */
  const checkAllPermissions = (
    module: ModuleTypeEnum,
    actions: PermissionActionEnum[],
  ): boolean => {
    return actions.every(action => checkPermission(module, action))
  }

  return {
    checkPermission,
    checkAnyPermission,
    checkAllPermissions,
    isLoadingPermissions: isLoadingMemberPermissions,
    hasPermission: checkPermission,
    // Org owner/admin. Some surfaces are gated on the org role itself rather
    // than a module permission — e.g. endpoints guarded by
    // @Access(AccessType.ORGANIZATION, OWNER, ADMIN) — and need this directly.
    isAdminOrOwner,
  }
}
